import { AdvancedMarker, Pin, useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef } from 'react';

import type { DndAdvancedMarkerProps } from '.';

/**
 * A draggable AdvancedMarker that also supports setting its position by
 * long-pressing on the map.
 *
 * Features:
 * - Drag the marker to update its position (`onDragEnd`).
 * - Long press on the map for `longPressMs` to set the marker at the press location.
 *   The long-press is canceled if the map is dragged/panned or the press ends early.
 *
 * Requirements:
 * - Must be rendered within a GoogleMap provider/context.
 *
 * @param dndAdvancedMarkerProps - Component props.
 * @returns An AdvancedMarker or null if no position is provided.
 */
export function DndAdvancedMarker(dndAdvancedMarkerProps: DndAdvancedMarkerProps) {
  const { position, onSetPosition, longPressMs = 600 } = dndAdvancedMarkerProps;

  const map = useMap();
  const timerRef = useRef<number | null>(null);
  const downLatLngRef = useRef<google.maps.LatLng | null>(null);
  const downTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!map) return;

    const onMouseDown = (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      downLatLngRef.current = e.latLng;
      downTimeRef.current = Date.now();
      timerRef.current = window.setTimeout(() => {
        // long press trigger
        if (downLatLngRef.current) {
          const pos = downLatLngRef.current.toJSON();
          onSetPosition(pos);
        }
      }, longPressMs);
    };

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const onMouseUp = (_event: google.maps.MapMouseEvent) => {
      const duration = Date.now() - downTimeRef.current;
      // If released before threshold, cancel
      if (duration < longPressMs) {
        clearTimer();
      }
    };

    const onDrag = () => {
      // if map pans / user drags, cancel long press
      clearTimer();
    };

    const mdListener = map.addListener('mousedown', onMouseDown);
    const muListener = map.addListener('mouseup', onMouseUp);
    const touchStartListener = map.addListener('touchstart', onMouseDown);
    const touchEndListener = map.addListener('touchend', onMouseUp);
    const dragListener = map.addListener('drag', onDrag);

    return () => {
      mdListener.remove();
      muListener.remove();
      touchStartListener.remove();
      touchEndListener.remove();
      dragListener.remove();
      clearTimer();
    };
  }, [map, onSetPosition, longPressMs]);

  if (!position) {
    return null;
  }

  return (
    <AdvancedMarker
      position={position}
      draggable
      onDragEnd={(event: google.maps.MapMouseEvent) => {
        if (!event.latLng) return;
        const pos = event.latLng.toJSON();
        onSetPosition(pos);
      }}
    >
      <Pin />
    </AdvancedMarker>
  );
}
